import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './CVE.css';
import axiosInstance from '../../api';

const SEVERITY_CONFIG = {
  CRITICAL: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  HIGH:     { color: '#ea580c', bg: '#fff7ed', border: '#fdba74' },
  MEDIUM:   { color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  LOW:      { color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
};

const STATUS_CONFIG = {
  'Analyzed':            { color: '#15803d', bg: '#dcfce7' },
  'Modified':            { color: '#1d4ed8', bg: '#dbeafe' },
  'Awaiting Analysis':   { color: '#b45309', bg: '#fef3c7' },
  'Received':            { color: '#7c3aed', bg: '#ede9fe' },
  'Undergoing Analysis': { color: '#0369a1', bg: '#e0f2fe' },
  'Rejected':            { color: '#6b7280', bg: '#f3f4f6' },
};

function SeverityBadge({ severity }) {
  const { t } = useTranslation();
  if (!severity) return <span className="cve-badge cve-badge-na">{t('CVE.notAvailable')}</span>;
  const cfg = SEVERITY_CONFIG[severity.toUpperCase()] || {};
  return (
    <span className="cve-badge" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {severity}
    </span>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const cfg = STATUS_CONFIG[status] || { color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="cve-badge" style={{ color: cfg.color, background: cfg.bg }}>
      {status}
    </span>
  );
}

function CVE() {
  const { t } = useTranslation();
  const [cveData,       setCveData]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [totalCount,    setTotalCount]    = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);
  const [currentPage,   setCurrentPage]   = useState(0);
  const [expandedRow,   setExpandedRow]   = useState(null);
  const [search,        setSearch]        = useState('');
  const [filterSev,     setFilterSev]     = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');

  const pageSize = 15;

  const fetchPage = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axiosInstance.get('/cve/all', {
        params: { page, size: pageSize },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      setCveData(data.cveList || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(Math.ceil((data.totalCount || 0) / pageSize));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(currentPage); }, [currentPage, fetchPage]);

  const filtered = cveData.filter(cve => {
    const matchSearch = !search ||
      cve.cveId?.toLowerCase().includes(search.toLowerCase()) ||
      cve.description?.toLowerCase().includes(search.toLowerCase());
    const matchSev    = !filterSev    || (cve.baseSeverity || '').toLowerCase() === filterSev.toLowerCase();
    const matchStatus = !filterStatus || cve.vulnStatus === filterStatus;
    return matchSearch && matchSev && matchStatus;
  });

  const severityCounts = cveData.reduce((acc, c) => {
    const s = (c.baseSeverity || 'Unknown').toUpperCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  if (error) return (
    <div className="cve-error">
      <i className="fas fa-exclamation-circle" /> {t('CVE.errorLoading', { error })}
    </div>
  );

  return (
    <div className="cve-page">
      {/* Stats row */}
      <div className="cve-stats">
        <div className="cve-stat-card cve-stat-total">
          <div className="cve-stat-value">{totalCount.toLocaleString()}</div>
          <div className="cve-stat-label">{t('CVE.totalCves')}</div>
        </div>
        {Object.entries(SEVERITY_CONFIG).map(([sev, cfg]) => (
          <div key={sev} className="cve-stat-card" style={{ borderTop: `3px solid ${cfg.color}` }}>
            <div className="cve-stat-value" style={{ color: cfg.color }}>
              {severityCounts[sev] || 0}
            </div>
            <div className="cve-stat-label">{sev}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="cve-toolbar">
        <div className="cve-search-wrap">
          <i className="fas fa-search cve-search-icon" />
          <input
            className="cve-search"
            placeholder={t('CVE.searchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setExpandedRow(null); }}
          />
          {search && (
            <button className="cve-search-clear" onClick={() => setSearch('')}>
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <select className="cve-filter" value={filterSev} onChange={e => setFilterSev(e.target.value)}>
          <option value="">{t('CVE.allSeverities')}</option>
          {Object.keys(SEVERITY_CONFIG).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="cve-filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">{t('CVE.allStatuses')}</option>
          {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="cve-result-count">{t('CVE.resultCount', { count: filtered.length })}</span>
      </div>

      {/* Table */}
      <div className="cve-table-wrap">
        {loading ? (
          <div className="cve-loading">
            <i className="fas fa-circle-notch fa-spin" /> {t('CVE.loading')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="cve-empty">{t('CVE.emptyState')}</div>
        ) : (
          <table className="cve-table">
            <thead>
              <tr>
                <th style={{ width: '14%' }}>{t('CVE.colCveId')}</th>
                <th style={{ width: '10%' }}>{t('CVE.colSeverity')}</th>
                <th style={{ width: '8%'  }}>{t('CVE.colScore')}</th>
                <th style={{ width: '14%' }}>{t('CVE.colStatus')}</th>
                <th style={{ width: '12%' }}>{t('CVE.colPublished')}</th>
                <th style={{ width: '12%' }}>{t('CVE.colModified')}</th>
                <th>{t('CVE.colCwes')}</th>
                <th style={{ width: '3%'  }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cve, i) => (
                <React.Fragment key={cve.id || i}>
                  <tr
                    className={`cve-row${expandedRow === i ? ' cve-row-expanded' : ''}`}
                    onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                  >
                    <td>
                      <a
                        href={`https://nvd.nist.gov/vuln/detail/${cve.cveId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cve-id-link"
                        onClick={e => e.stopPropagation()}
                      >
                        {cve.cveId}
                      </a>
                    </td>
                    <td><SeverityBadge severity={cve.baseSeverity} /></td>
                    <td>
                      {cve.impactScore != null
                        ? <span className="cve-score">{cve.impactScore.toFixed(1)}</span>
                        : <span className="cve-score-na">—</span>
                      }
                    </td>
                    <td><StatusBadge status={cve.vulnStatus} /></td>
                    <td className="cve-date">
                      {cve.published ? new Date(cve.published).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                    </td>
                    <td className="cve-date">
                      {cve.lastModified ? new Date(cve.lastModified).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                    </td>
                    <td>
                      <div className="cve-cwe-list">
                        {(cve.cweIds || []).slice(0, 3).map(cwe => (
                          <a
                            key={cwe}
                            href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cve-cwe-tag"
                            onClick={e => e.stopPropagation()}
                          >
                            {cwe}
                          </a>
                        ))}
                        {(cve.cweIds || []).length > 3 && (
                          <span className="cve-cwe-more">+{cve.cweIds.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <i className={`fas fa-chevron-${expandedRow === i ? 'up' : 'down'} cve-chevron`} />
                    </td>
                  </tr>
                  {expandedRow === i && (
                    <tr className="cve-detail-row">
                      <td colSpan={8}>
                        <div className="cve-detail">
                          <div className="cve-detail-label">{t('CVE.descriptionLabel')}</div>
                          <p className="cve-detail-text">{cve.description || t('CVE.noDescription')}</p>
                          {(cve.cweIds || []).length > 0 && (
                            <>
                              <div className="cve-detail-label" style={{ marginTop: 10 }}>{t('CVE.weaknessClassifications')}</div>
                              <div className="cve-cwe-list" style={{ marginTop: 4 }}>
                                {cve.cweIds.map(cwe => (
                                  <a
                                    key={cwe}
                                    href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cve-cwe-tag"
                                  >
                                    {cwe}
                                  </a>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="cve-pagination">
          <button
            className="cve-page-btn"
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
          >
            <i className="fas fa-angle-double-left" />
          </button>
          <button
            className="cve-page-btn"
            onClick={() => setCurrentPage(p => p - 1)}
            disabled={currentPage === 0}
          >
            <i className="fas fa-angle-left" />
          </button>
          <span className="cve-page-info">
            {t('CVE.pageInfo', { current: currentPage + 1, total: totalPages, count: totalCount.toLocaleString() })}
          </span>
          <button
            className="cve-page-btn"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            <i className="fas fa-angle-right" />
          </button>
          <button
            className="cve-page-btn"
            onClick={() => setCurrentPage(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
          >
            <i className="fas fa-angle-double-right" />
          </button>
        </div>
      )}
    </div>
  );
}

export default CVE;
